import { useMemo } from "react";
import GroupedSlaBarChart, {
  GroupedSlaChartConfig,
} from "./GroupedSlaBarChart";

export interface WorkflowStageItem {
  name: string;
  statusType: string;
}

interface IProps {
  data: any;
  startDate?: string;
  endDate?: string;
  teamId?: string | null;
  workflowId?: string | null;
  workflowStages?: WorkflowStageItem[];
}

const WORKFLOW_STAGE_CONFIG: GroupedSlaChartConfig = {
  title: "Workflow SL",
  modalSubtitle: "Workflow within SL vs breach by date",
  emptyLabel: "workflow SL",
  sampleCategories: [
    "Requirement discussion",
    "Development",
    "Testing",
    "Resolved",
  ],
  colorPalette: [
    { dark: "#0d9488", light: "#99f6e4" },
    { dark: "#e11d48", light: "#fda4af" },
    { dark: "#4f46e5", light: "#c7d2fe" },
    { dark: "#ca8a04", light: "#fef08a" },
  ],
  apiArrayKeys: [
    "workflow_sl_graph",
    "daily_workflow_stage_resolution_sla",
    "workflow_stage_resolution_sla_daily",
    "workflow_stage_wise_resolution_sla",
    "resolution_sla_by_workflow_stage",
    "daily_resolution_sla_by_stage",
  ],
  nestedItemKeys: ["stages", "workflow_stages", "stage_data", "data"],
  categoryNameFields: [
    "stage",
    "workflow_stage",
    "status",
    "status_name",
    "stage_name",
    "name",
  ],
  dailyGraphFields: {
    resolvedKeys: ["workflow_sl_resolved"],
    breachedKeys: ["workflow_sl_breached"],
    itemResolvedKeys: ["workflow_sl_resolved"],
    itemBreachedKeys: ["workflow_sl_breached"],
  },
  excludedRootKeys: [
    "card_count",
    "tag_sl_graph",
    "disposition_sl_graph",
    "average_resolution_time",
    "resolution_beyond_sla",
    "sla_met_count",
    "sla_breached_count",
    "daily_tag_resolution_sla",
    "tag_resolution_sla_daily",
    "tag_wise_resolution_sla",
    "resolution_sla_by_tag",
    "daily_resolution_sla_by_tag",
  ],
  clickToExpand: {
    graphName: "workflow_sl_graph",
    categoryIdField: "status_id",
    categoryIdParam: "status_id",
    resolvedValue: "resolved",
    breachedValue: "breached",
    workflowMode: true,
  },
};

const UNIQUE_WORKFLOW_STAGE_COLORS = [
  { dark: "#0d6efd", light: "#93c5fd" },
  { dark: "#0891b2", light: "#a5f3fc" },
  { dark: "#7c3aed", light: "#c4b5fd" },
  { dark: "#db2777", light: "#f9a8d4" },
  { dark: "#ea580c", light: "#fdba74" },
  { dark: "#ca8a04", light: "#fde047" },
  { dark: "#65a30d", light: "#bef264" },
  { dark: "#059669", light: "#6ee7b7" },
  { dark: "#0f766e", light: "#5eead4" },
  { dark: "#4f46e5", light: "#a5b4fc" },
  { dark: "#be185d", light: "#f0abfc" },
  { dark: "#b45309", light: "#fcd34d" },
  { dark: "#dc2626", light: "#fca5a5" },
  { dark: "#475569", light: "#cbd5e1" },
  { dark: "#9333ea", light: "#d8b4fe" },
];

export const getWorkflowStageItems = (
  workflowResults: any[] | undefined,
  workflowId: string | number | undefined,
): WorkflowStageItem[] => {
  if (!workflowResults?.length || workflowId == null || workflowId === "") {
    return [];
  }

  const workflow = workflowResults.find(
    (item: any) => String(item.id) === String(workflowId),
  );
  if (!workflow?.all_statuses) return [];

  return workflow.all_statuses
    .map((stage: any) => {
      const name =
        stage.status_type === "active" || stage.status_type === "reopen"
          ? typeof stage.status === "string"
            ? stage.status
            : ""
          : (stage.status?.status ?? "");

      return {
        name: String(name).trim(),
        statusType: String(stage.status_type ?? ""),
      };
    })
    .filter((stage: WorkflowStageItem) => stage.name.length > 0);
};

const buildStageColorConfig = (stages: WorkflowStageItem[]) => {
  const colorPalette = stages.map(
    (stage, index) =>
      UNIQUE_WORKFLOW_STAGE_COLORS[
        index % UNIQUE_WORKFLOW_STAGE_COLORS.length
      ],
  );

  const categoryColors = Object.fromEntries(
    stages.map((stage, index) => [
      stage.name,
      UNIQUE_WORKFLOW_STAGE_COLORS[
        index % UNIQUE_WORKFLOW_STAGE_COLORS.length
      ],
    ]),
  );

  return { colorPalette, categoryColors };
};

const buildStageClickMeta = (stages: WorkflowStageItem[]) =>
  Object.fromEntries(
    stages.map((stage) => {
      const isOpenType =
        stage.statusType === "active" ||
        stage.statusType === "reopen" ||
        stage.statusType === "open";

      return [
        stage.name,
        {
          statusType: isOpenType ? "open" : stage.statusType,
          useStatusId: !isOpenType && stage.statusType === "wip",
        },
      ];
    }),
  );

const WorkflowStageResolutionSLA: React.FC<IProps> = ({
  workflowStages,
  ...props
}) => {
  const configOverrides = useMemo(() => {
    if (!workflowStages?.length) return undefined;

    const { colorPalette, categoryColors } =
      buildStageColorConfig(workflowStages);

    return {
      sampleCategories: workflowStages.map((stage) => stage.name),
      dailyCategoryNames: workflowStages.map((stage) => stage.name),
      colorPalette,
      categoryColors,
      categoryClickMeta: buildStageClickMeta(workflowStages),
    };
  }, [workflowStages]);

  return (
    <GroupedSlaBarChart
      {...props}
      config={WORKFLOW_STAGE_CONFIG}
      configOverrides={configOverrides}
    />
  );
};

export default WorkflowStageResolutionSLA;
